export {
	DEFAULT_MAX_BYTES,
	DEFAULT_MAX_LINES,
	formatMiddleOmissionMarker,
	formatSize,
	type TruncationOptions,
	type TruncationResult,
	truncateHead,
	truncateKnownHeadTail,
	truncateLine,
	truncateMiddle,
	truncateTail,
} from "@caupulican/pi-agent-core/node";
export {
	type ArtifactRetrieveToolDetails,
	type ArtifactRetrieveToolInput,
	type ArtifactRetrieveToolOptions,
	createArtifactRetrieveTool,
	createArtifactRetrieveToolDefinition,
} from "./artifact-retrieve.ts";
export {
	type BashOperations,
	type BashSpawnContext,
	type BashSpawnHook,
	type BashToolDetails,
	type BashToolInput,
	type BashToolOptions,
	createBashTool,
	createBashToolDefinition,
	createLocalBashOperations,
	createLocalPlatformShellOperations,
	createLocalPowerShellOperations,
} from "./bash.ts";
export {
	CONTEXT_SCOUT_GUIDANCE,
	type ContextScoutToolDependencies,
	type ContextScoutToolDetails,
	type ContextScoutToolInput,
	createContextScoutToolDefinition,
	formatScoutResult,
} from "./context-scout.ts";
export {
	createEditTool,
	createEditToolDefinition,
	type EditOperations,
	type EditToolDetails,
	type EditToolInput,
	type EditToolOptions,
} from "./edit.ts";
export {
	createExtensionifyTool,
	createExtensionifyToolDefinition,
	type ExtensionifyInput,
	type ExtensionifyReport,
	type ExtensionifyToolDetails,
	type ExtensionifyToolOptions,
} from "./extensionify.ts";
export {
	createFileFailureRecoveryAuthority,
	type FileFailureRecoveryAuthority,
} from "./file-failure-recovery.ts";
export {
	type FileContentReference,
	FileMutationIntentController,
	type FileMutationIntentControllerOptions,
	type FileMutationIntentOperations,
	type FileMutationKind,
	type FileMutationLease,
	type FileMutationPayloadReference,
	FileMutationPreflightError,
	type FileMutationPreflightFailureReason,
	type FilePathIdentity,
	type FilePathInspection,
	localFileMutationIntentOperations,
} from "./file-mutation-intent.ts";
export { withFileMutationQueue } from "./file-mutation-queue.ts";
export {
	createFindTool,
	createFindToolDefinition,
	type FindOperations,
	type FindToolDetails,
	type FindToolInput,
	type FindToolOptions,
} from "./find.ts";
export {
	createGrepTool,
	createGrepToolDefinition,
	type GrepOperations,
	type GrepToolDetails,
	type GrepToolInput,
	type GrepToolOptions,
} from "./grep.ts";
export {
	createLsTool,
	createLsToolDefinition,
	type LsOperations,
	type LsToolDetails,
	type LsToolInput,
	type LsToolOptions,
} from "./ls.ts";
export {
	createPythonTool,
	createPythonToolDefinition,
	DEFAULT_PYTHON_TIMEOUT_SECONDS,
	MAX_PYTHON_OUTPUT_BYTES,
	MAX_PYTHON_TIMEOUT_SECONDS,
	type PythonExecutionRequest,
	type PythonExecutionResult,
	type PythonOperations,
	type PythonToolDetails,
	type PythonToolInput,
	type PythonToolOptions,
	resolvePythonToolPath,
} from "./python.ts";
export {
	createReadTool,
	createReadToolDefinition,
	type ReadOperations,
	type ReadToolDetails,
	type ReadToolInput,
	type ReadToolOptions,
} from "./read.ts";
export {
	createSecretStoreToolDefinition,
	type SecretStoreStatus,
	type SecretStoreToolDetails,
	type SecretStoreToolInput,
	type SecretStoreToolOptions,
} from "./secret-store.ts";
export {
	createSkillVaultToolDefinition,
	type SkillToolDetails,
	type SkillToolInput,
} from "./skill.ts";
export {
	createSkillAuditTool,
	createSkillAuditToolDefinition,
	jaccard,
	type SkillAuditInput,
	type SkillAuditReport,
	type SkillAuditToolDetails,
	type SkillAuditToolOptions,
	tokenize,
} from "./skill-audit.ts";
export {
	createSkillifyTool,
	createSkillifyToolDefinition,
	type SkillifyInput,
	type SkillifyReport,
	type SkillifyToolDetails,
	type SkillifyToolOptions,
} from "./skillify.ts";
export {
	createWriteTool,
	createWriteToolDefinition,
	type WriteOperations,
	type WriteToolDetails,
	type WriteToolInput,
	type WriteToolOptions,
} from "./write.ts";

import type { AgentTool } from "@caupulican/pi-agent-core";
import {
	type ArtifactRetrieveToolOptions,
	createArtifactRetrieveTool,
	createArtifactRetrieveToolDefinition,
} from "./artifact-retrieve.ts";
import { type BashToolOptions, createBashTool, createBashToolDefinition } from "./bash.ts";
import { createEditTool, createEditToolDefinition, type EditToolOptions } from "./edit.ts";
import {
	createDefaultExtensionifyRuntimeOptions,
	createExtensionifyTool,
	createExtensionifyToolDefinition,
	type ExtensionifyToolOptions,
} from "./extensionify.ts";
import { FileMutationIntentController } from "./file-mutation-intent.ts";
import { createFindTool, createFindToolDefinition, type FindToolOptions } from "./find.ts";
import { createGrepTool, createGrepToolDefinition, type GrepToolOptions } from "./grep.ts";
import { createLsTool, createLsToolDefinition, type LsToolOptions } from "./ls.ts";
import { createPythonTool, createPythonToolDefinition, type PythonToolOptions } from "./python.ts";
import { createReadTool, createReadToolDefinition, type ReadToolOptions } from "./read.ts";
import { createSkillAuditTool, createSkillAuditToolDefinition, type SkillAuditToolOptions } from "./skill-audit.ts";
import { createSkillifyTool, createSkillifyToolDefinition, type SkillifyToolOptions } from "./skillify.ts";
import { createToolDefinitionWithRuntime, type ToolDef, type ToolName } from "./tool-definition-factory.ts";
import { createWriteTool, createWriteToolDefinition, type WriteToolOptions } from "./write.ts";

export { allToolNames, type ToolDef, type ToolName } from "./tool-definition-factory.ts";

export type Tool = AgentTool<any>;

export interface ToolsOptions {
	read?: ReadToolOptions;
	bash?: BashToolOptions;
	python?: PythonToolOptions;
	write?: WriteToolOptions;
	edit?: EditToolOptions;
	grep?: GrepToolOptions;
	find?: FindToolOptions;
	ls?: LsToolOptions;
	skill_audit?: SkillAuditToolOptions;
	skillify?: SkillifyToolOptions;
	extensionify?: ExtensionifyToolOptions;
	artifact_retrieve?: ArtifactRetrieveToolOptions;
}

function withSharedFileMutationIntents(options?: ToolsOptions): ToolsOptions {
	const writeController = options?.write?.intentController;
	const editController = options?.edit?.intentController;
	if ((options?.write?.operations || options?.edit?.operations) && !writeController && !editController) {
		throw new Error("Custom file operations require an explicit shared file mutation intent controller.");
	}
	if (writeController && editController && writeController !== editController) {
		throw new Error("Write and edit tools in one surface must share one file mutation intent controller.");
	}
	const intentController = writeController ?? editController ?? new FileMutationIntentController();
	return {
		...options,
		write: { ...options?.write, intentController },
		edit: { ...options?.edit, intentController },
	};
}

export function createToolDefinition(toolName: ToolName, cwd: string, options?: ToolsOptions): ToolDef {
	return createToolDefinitionWithRuntime(toolName, cwd, {
		...options,
		extensionify: createDefaultExtensionifyRuntimeOptions(options?.extensionify),
	});
}

export function createTool(toolName: ToolName, cwd: string, options?: ToolsOptions): Tool {
	switch (toolName) {
		case "read":
			return createReadTool(cwd, options?.read);
		case "bash":
			return createBashTool(cwd, options?.bash);
		case "python":
			return createPythonTool(cwd, options?.python);
		case "edit":
			return createEditTool(cwd, options?.edit);
		case "write":
			return createWriteTool(cwd, options?.write);
		case "grep":
			return createGrepTool(cwd, options?.grep);
		case "find":
			return createFindTool(cwd, options?.find);
		case "ls":
			return createLsTool(cwd, options?.ls);
		case "skill_audit":
			return createSkillAuditTool(cwd, options?.skill_audit);
		case "skillify":
			return createSkillifyTool(cwd, options?.skillify);
		case "extensionify":
			return createExtensionifyTool(cwd, options?.extensionify);
		case "artifact_retrieve":
			return createArtifactRetrieveTool(cwd, options?.artifact_retrieve);
		default:
			throw new Error(`Unknown tool name: ${toolName}`);
	}
}

export function createCodingToolDefinitions(
	cwd: string,
	options?: ToolsOptions,
	platform: NodeJS.Platform = process.platform,
): ToolDef[] {
	const sharedOptions = withSharedFileMutationIntents(options);
	const bashOptions: BashToolOptions = { ...options?.bash, platform };
	return [
		createReadToolDefinition(cwd, options?.read),
		createBashToolDefinition(cwd, bashOptions),
		createPythonToolDefinition(cwd, options?.python),
		createEditToolDefinition(cwd, sharedOptions.edit),
		createWriteToolDefinition(cwd, sharedOptions.write),
	];
}

export function createReadOnlyToolDefinitions(cwd: string, options?: ToolsOptions): ToolDef[] {
	return [
		createReadToolDefinition(cwd, options?.read),
		createGrepToolDefinition(cwd, options?.grep),
		createFindToolDefinition(cwd, options?.find),
		createLsToolDefinition(cwd, options?.ls),
	];
}

export function createAllToolDefinitions(
	cwd: string,
	options?: ToolsOptions,
	platform: NodeJS.Platform = process.platform,
): Partial<Record<ToolName, ToolDef>> {
	const sharedOptions = withSharedFileMutationIntents(options);
	const bashOptions: BashToolOptions = { ...options?.bash, platform };
	return {
		read: createReadToolDefinition(cwd, options?.read),
		bash: createBashToolDefinition(cwd, bashOptions),
		python: createPythonToolDefinition(cwd, options?.python),
		edit: createEditToolDefinition(cwd, sharedOptions.edit),
		write: createWriteToolDefinition(cwd, sharedOptions.write),
		grep: createGrepToolDefinition(cwd, options?.grep),
		find: createFindToolDefinition(cwd, options?.find),
		ls: createLsToolDefinition(cwd, options?.ls),
		skill_audit: createSkillAuditToolDefinition(cwd, options?.skill_audit),
		skillify: createSkillifyToolDefinition(cwd, options?.skillify),
		extensionify: createExtensionifyToolDefinition(cwd, options?.extensionify),
		artifact_retrieve: createArtifactRetrieveToolDefinition(cwd, options?.artifact_retrieve),
	};
}

export function createCodingTools(
	cwd: string,
	options?: ToolsOptions,
	platform: NodeJS.Platform = process.platform,
): Tool[] {
	const sharedOptions = withSharedFileMutationIntents(options);
	const bashOptions: BashToolOptions = { ...options?.bash, platform };
	return [
		createReadTool(cwd, options?.read),
		createBashTool(cwd, bashOptions),
		createPythonTool(cwd, options?.python),
		createEditTool(cwd, sharedOptions.edit),
		createWriteTool(cwd, sharedOptions.write),
	];
}

export function createReadOnlyTools(cwd: string, options?: ToolsOptions): Tool[] {
	return [
		createReadTool(cwd, options?.read),
		createGrepTool(cwd, options?.grep),
		createFindTool(cwd, options?.find),
		createLsTool(cwd, options?.ls),
	];
}

export function createAllTools(
	cwd: string,
	options?: ToolsOptions,
	platform: NodeJS.Platform = process.platform,
): Partial<Record<ToolName, Tool>> {
	const sharedOptions = withSharedFileMutationIntents(options);
	const bashOptions: BashToolOptions = { ...options?.bash, platform };
	return {
		read: createReadTool(cwd, options?.read),
		bash: createBashTool(cwd, bashOptions),
		python: createPythonTool(cwd, options?.python),
		edit: createEditTool(cwd, sharedOptions.edit),
		write: createWriteTool(cwd, sharedOptions.write),
		grep: createGrepTool(cwd, options?.grep),
		find: createFindTool(cwd, options?.find),
		ls: createLsTool(cwd, options?.ls),
		skill_audit: createSkillAuditTool(cwd, options?.skill_audit),
		skillify: createSkillifyTool(cwd, options?.skillify),
		extensionify: createExtensionifyTool(cwd, options?.extensionify),
		artifact_retrieve: createArtifactRetrieveTool(cwd, options?.artifact_retrieve),
	};
}
